/**
 * B332 / B314 — today's session as the hero (and the coach insight that quotes
 * its title) should read it for this athlete.
 *
 * `useTodaySession` builds the title's distance phrase from the plan text and
 * only ever states it in miles ("Easy run, 6.3 miles."). A km athlete is shown
 * the same run in km everywhere else on Today, so the phrase is converted here
 * at display time. Storage and the hook stay as they are.
 *
 * B338 fix 1 (critic M1) — the subtitle is the stored plan text ("6 mi easy at
 * 9:10/mi") and rides the coach card body and the motivation prompt, so it is
 * read through `planTextInUnit`, the very conversion the week section's day
 * panel uses (`useWeeklyPlan` imports it from here). A pace is left as written,
 * as it is there.
 *
 * B338 fix 2 (wave 3 critic M1) — a text that states one distance in BOTH units
 * ("7 km (4.3 mi) easy", "6 mi / 9.7 km") shows the figure as written in the
 * athlete's unit, once. `planTextInUnit` collapses the restatement; the hero phrase
 * takes the figure the hook recorded for each unit (`titleEmFigures`), never a
 * conversion of the other unit's rounding.
 *
 * A session that needs no change comes back as the SAME object, so a mile
 * athlete on a miles-authored week gets a byte-identical render. A phrase that is
 * not "N miles" or "N km" (a rep set such as "5x400m", or no phrase at all) is
 * left exactly as it is.
 */
import type { TodaySession } from '../../fixtures/plan';
import { fromMiles, toMiles, type DistanceUnit } from '../../lib/actualForPlanDay';
import type { TodaySessionWithFigures } from '../../hooks/useTodaySession';

const MILES_PHRASE_RE = /^(\d+(?:\.\d+)?) miles$/;
// B338 — the hook now also states a km-only plan text as "N km"; a mile
// athlete reads that one in miles.
const KM_PHRASE_RE = /^(\d+(?:\.\d+)?) km$/;

/** One decimal, no trailing ".0" ("10", "6.3"). */
function roundOne(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * B309 fix round — the plan text for the details panel with every km / mile
 * distance stated in the athlete's own unit ("10.2 km easy" reads "6.3 mi easy"
 * for a mile athlete). Display only: the stored text is never touched. Meters
 * ("800m") and paces ("3:25/km", "8:30 per mile") are left exactly as written
 * (how paces are resolved is B313), as is any token already in the target unit.
 */
const TEXT_DISTANCE_RE =
  /(^|[^\d:.,/])(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?\s*(miles?|mi|kilometers?|kilometres?|km)(?![a-z/])/gi;

/**
 * B338 fix 2 (critic M1) — one distance stated in both units, "7 km (4.3 mi)" or
 * "6 mi / 9.7 km": the figure written in the athlete's unit stays as written and the
 * other is dropped. The second figure must close the bracket right after its unit
 * (or follow a slash), so "5 km (2 mi cooldown)" is two distances and each is read
 * by the per-token conversion below. Same lead rule as TEXT_DISTANCE_RE.
 */
const EN_DASH = String.fromCharCode(0x2013); // a range may be written 4-5 or 4<en dash>5, as in TEXT_DISTANCE_RE
const DIST_FIGURE =
  String.raw`\d+(?:\.\d+)?(?:\s*[-${EN_DASH}]\s*\d+(?:\.\d+)?)?\s*(?:miles?|mi|kilometers?|kilometres?|km)`;
const RESTATED_RE = new RegExp(
  String.raw`(^|[^\d:.,/])(${DIST_FIGURE})(?:\s*\(\s*(${DIST_FIGURE})\s*\)|\s*\/\s*(${DIST_FIGURE})(?![a-z/]))`,
  'gi',
);
const isMileFigure = (figure: string): boolean => /(?:miles?|mi)$/i.test(figure);

export function planTextInUnit(text: string | null | undefined, unit: DistanceUnit): string | null {
  if (!text) return text ?? null;
  return text
    .replace(
      RESTATED_RE,
      (
        whole: string,
        lead: string,
        first: string,
        bracketed: string | undefined,
        slashed: string | undefined,
      ) => {
        const second = (bracketed ?? slashed) as string;
        const firstIsMile = isMileFigure(first);
        if (firstIsMile === isMileFigure(second)) return whole;
        return `${lead}${firstIsMile === (unit === 'mi') ? first : second}`;
      },
    )
    .replace(
      TEXT_DISTANCE_RE,
      (whole: string, lead: string, a: string, b: string | undefined, unitRaw: string) => {
        const isMile = unitRaw.toLowerCase().startsWith('mi');
        if ((unit === 'mi') === isMile) return whole;
        const convert = (n: string) =>
          roundOne(isMile ? fromMiles(Number(n), 'km') : toMiles(Number(n), 'km'));
        return `${lead}${convert(a)}${b !== undefined ? ` to ${convert(b)}` : ''} ${unit}`;
      },
    );
}

function titleEmInUnit(
  titleEm: string,
  unit: DistanceUnit,
  figures?: TodaySessionWithFigures['titleEmFigures'],
): string {
  // B338 fix 2 — the plan text wrote this distance in both units: read the figure it
  // wrote for this athlete's unit, never the other unit's rounding converted.
  if (figures && (MILES_PHRASE_RE.test(titleEm) || KM_PHRASE_RE.test(titleEm))) {
    return unit === 'km' ? `${figures.km} km` : `${figures.mi} miles`;
  }
  if (unit !== 'km') {
    const k = KM_PHRASE_RE.exec(titleEm);
    if (!k) return titleEm;
    const mi = Math.round(toMiles(Number(k[1]), 'km') * 10) / 10;
    return `${mi} miles`;
  }
  const m = MILES_PHRASE_RE.exec(titleEm);
  if (!m) return titleEm;
  const km = Math.round(fromMiles(Number(m[1]), 'km') * 10) / 10;
  return `${km} km`;
}

export function sessionInUnit(
  session: TodaySession | null,
  unit: DistanceUnit,
): TodaySession | null {
  if (!session) return session;
  const titleEm = titleEmInUnit(
    session.titleEm,
    unit,
    (session as TodaySessionWithFigures).titleEmFigures,
  );
  const subtitle = planTextInUnit(session.subtitle, unit) ?? session.subtitle;
  if (titleEm === session.titleEm && subtitle === session.subtitle) return session;
  return { ...session, titleEm, subtitle };
}
